import {
    AlertTriangle,
    Calculator,
    ChevronLeft,
    ChevronRight,
    Info,
    LoaderCircle,
    Plus,
    Sparkles,
    Trash2,
    Zap,
} from 'lucide-react'
import debounce from 'lodash/debounce'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './AddIncentiveComponentScreen.module.css'

const COMPONENT_OPTIONS = [
    {
        type: 'SERVICE_COMMISSION',
        label: 'Service Commission',
        description: 'Flat % on all service revenue generated by the staff.',
    },
    {
        type: 'RETAIL_COMMISSION',
        label: 'Retail Commission',
        description: 'Flat % on retail/product sales by the staff.',
    },
    {
        type: 'CATEGORY_COMMISSION',
        label: 'Category Commission',
        description: 'Different % per service category (hair, skin, nail etc.).',
    },
    {
        type: 'SLAB_COMMISSION',
        label: 'Tiered Slab',
        description: 'Commission % increases as monthly revenue grows.',
    },
    {
        type: 'TARGET_BONUS',
        label: 'Target Bonus',
        description: 'Bonus unlocked when a revenue target is hit.',
    },
    {
        type: 'ATTENDANCE_BONUS',
        label: 'Attendance Bonus',
        description: 'Fixed bonus for full month attendance with no LWP.',
    },
    {
        type: 'CUSTOM_INCENTIVE',
        label: 'Custom Incentive',
        description: 'Any other commission or bonus rule.',
    },
]

const TYPE_COLORS = {
    SERVICE_COMMISSION: { color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION: { color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION: { color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS: { color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS: { color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE: { color: '#546e7a', bg: '#eceff1' },
}

const SUGGESTED_NAMES = {
    SERVICE_COMMISSION: 'Service Commission',
    RETAIL_COMMISSION: 'Retail Commission',
    CATEGORY_COMMISSION: 'Category Commission',
    SLAB_COMMISSION: 'Revenue Slab Commission',
    TARGET_BONUS: 'Monthly Target Bonus',
    ATTENDANCE_BONUS: 'Full Attendance Bonus',
    CUSTOM_INCENTIVE: 'Custom Incentive',
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return 0
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
}

const formatINR = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatSlabPreviewRange = (slab) => {
    const from = formatINR(slab.revenueFrom)
    const to = slab.revenueTo !== null && slab.revenueTo !== undefined
        ? formatINR(slab.revenueTo)
        : 'No limit'
    return `${from} – ${to}`
}

const SlabSimulationResult = ({ result }) => {
    if (!result) return null

    return (
        <div className={styles.simResult}>
            <div className={styles.simSummary}>
                <p className={styles.simLabel}>Revenue simulated</p>
                <p className={styles.simRevenue}>{formatINR(result.simulateRevenue)}</p>

                <p className={styles.simLabel}>Total commission earned</p>
                <p className={styles.simTotal}>{formatINR(result.totalCommission)}</p>

                <p className={styles.simEffective}>Effective rate: {Number(result.effectiveRate || 0).toFixed(4)}%</p>

                {result.capApplied && (
                    <p className={styles.capWarning}>
                        Cap applied — commission was {formatINR(result.commissionBeforeCap)}, reduced to {formatINR(result.cappedAt)}.
                    </p>
                )}

                {result.isMixedMode && (
                    <p className={styles.modeNote}>Mixed slab modes detected — each slab uses its own computation method.</p>
                )}
            </div>

            <div className={styles.simTable}>
                <p className={styles.simTableTitle}>Step-by-step breakdown</p>

                {result.slabMode === 'ON_INCREMENTAL' && (
                    <p className={styles.modeLegend}>Incremental mode — each bracket rate applies only within that bracket.</p>
                )}

                {result.slabMode === 'ON_TOTAL' && (
                    <p className={styles.modeLegend}>Total mode — matched slab rate applies to full revenue.</p>
                )}

                {(result.slabBreakdown || []).map((slab, index) => (
                    <div
                        key={`sim-${index}`}
                        className={`${styles.simRow} ${slab.isApplicable ? styles.simRowApplicable : ''} ${slab.isMatchedSlab ? styles.simRowMatched : ''}`}
                    >
                        <div>
                            <p className={styles.simRange}>{formatSlabPreviewRange(slab)}</p>
                            <p className={styles.simRate}>
                                {slab.commissionPercent}% {slab.slabMode === 'ON_TOTAL' ? '(On Total)' : ''}
                            </p>
                        </div>
                        <div className={styles.simRight}>
                            {slab.isApplicable ? (
                                <>
                                    <p className={styles.simBracket}>
                                        {slab.slabMode === 'ON_TOTAL'
                                            ? 'Full revenue'
                                            : `${formatINR(slab.revenueInBracket)} in bracket`}
                                    </p>
                                    <p className={styles.simEarned}>+ {formatINR(slab.commissionEarned)}</p>
                                </>
                            ) : (
                                <p className={styles.simNA}>{slab.slabMode === 'ON_TOTAL' ? 'Not matched' : 'Below bracket'}</p>
                            )}
                        </div>
                    </div>
                ))}

                <div className={styles.simTotalRow}>
                    <span>Total Commission</span>
                    <strong>{formatINR(result.totalCommission)}</strong>
                </div>
            </div>
        </div>
    )
}

const AddIncentiveComponentScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const routeTemplateName = location.state?.templateName || 'Commission Plan'
    const prefilledType = location.state?.prefilledType || ''

    const [selectedType, setSelectedType] = useState(prefilledType)
    const [formData, setFormData] = useState({
        componentName: SUGGESTED_NAMES[prefilledType] || '',
        computationBase: 'SERVICE_REVENUE',
        rateValue: '',
        targetAmount: '',
        targetPeriod: 'MONTHLY',
        bonusValue: '',
        bonusValueType: 'FLAT',
        minServiceCountForEligibility: 0,
        cappedAt: '',
        sortOrder: '',
        slabs: [{ slabSequence: 1, revenueFrom: 0, revenueTo: '', commissionPercent: '', slabMode: 'ON_INCREMENTAL' }],
        categoryRates: [{ serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' }],
    })
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [simulateRevenueInput, setSimulateRevenueInput] = useState('')
    const [simulationResult, setSimulationResult] = useState(null)
    const [isSimulating, setIsSimulating] = useState(false)

    const selectedTypeMeta = useMemo(
        () => COMPONENT_OPTIONS.find((item) => item.type === selectedType),
        [selectedType]
    )

    const simulateSlabPreview = useMemo(
        () =>
            debounce(async (slabs, revenue) => {
                if (!authCtx?.token || !activeStore?.storeIdentifier) return
                if (!revenue || parseFloat(revenue) < 0) return

                const validSlabs = slabs.filter(
                    (s) => s.revenueFrom !== '' && s.commissionPercent !== ''
                )
                if (validSlabs.length === 0) return

                const topSlabCount = validSlabs.filter(
                    (s) => s.revenueTo === null || s.revenueTo === ''
                ).length
                if (topSlabCount !== 1) return

                try {
                    setIsSimulating(true)

                    const response = await fetch(BASE_URL_API + 'simulateIncentiveComponentSlab', {
                        method: 'POST',
                        body: JSON.stringify({
                            token: authCtx.token,
                            storeIdentifier: activeStore.storeIdentifier,
                            simulateRevenue: parseFloat(revenue),
                            cappedAt: parseFloat(formData.cappedAt) || 0,
                            slabs: validSlabs.map((s, i) => ({
                                slabSequence: i + 1,
                                revenueFrom: parseFloat(s.revenueFrom) || 0,
                                revenueTo:
                                    s.revenueTo === null || s.revenueTo === ''
                                        ? null
                                        : parseFloat(s.revenueTo),
                                commissionPercent: parseFloat(s.commissionPercent) || 0,
                                slabMode: s.slabMode || 'ON_INCREMENTAL',
                            })),
                        }),
                    })

                    const respo = await response.json()
                    if (respo?.status === 'success') {
                        setSimulationResult(respo.response)
                    }
                } catch (error) {
                    console.warn('Slab simulation preview failed:', error)
                } finally {
                    setIsSimulating(false)
                }
            }, 500),
        [authCtx?.token, activeStore?.storeIdentifier] // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(() => {
        if (selectedType !== 'SLAB_COMMISSION') {
            setSimulationResult(null)
            setSimulateRevenueInput('')
            simulateSlabPreview.cancel()
            return
        }
        simulateSlabPreview(formData.slabs, simulateRevenueInput)
        return () => simulateSlabPreview.cancel()
    }, [formData.slabs, simulateRevenueInput, selectedType]) // eslint-disable-line react-hooks/exhaustive-deps

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const handleTypeSelect = (type) => {
        setSelectedType(type)
        setErrors({})
        setFormData((prev) => ({
            ...prev,
            componentName: prev.componentName.trim() ? prev.componentName : (SUGGESTED_NAMES[type] || ''),
            computationBase: type === 'ATTENDANCE_BONUS' ? 'FIXED' : 'SERVICE_REVENUE',
            targetPeriod: 'MONTHLY',
            bonusValueType: 'FLAT',
            slabs: [{ slabSequence: 1, revenueFrom: 0, revenueTo: '', commissionPercent: '', slabMode: 'ON_INCREMENTAL' }],
            categoryRates: [{ serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' }],
        }))
    }

    const addSlabRow = () => {
        setFormData((prev) => {
            const lastSlab = prev.slabs[prev.slabs.length - 1]
            const newFrom =
                lastSlab?.revenueTo !== null &&
                lastSlab?.revenueTo !== '' &&
                lastSlab?.revenueTo !== undefined
                    ? lastSlab.revenueTo
                    : ''
            return {
                ...prev,
                slabs: [
                    ...prev.slabs,
                    {
                        slabSequence: prev.slabs.length + 1,
                        revenueFrom: newFrom,
                        revenueTo: '',
                        commissionPercent: '',
                        slabMode: 'ON_INCREMENTAL',
                    },
                ],
            }
        })
    }

    const removeSlabRow = (index) => {
        setFormData((prev) => {
            const next = prev.slabs.filter((_, rowIndex) => rowIndex !== index)
                .map((row, rowIndex) => ({ ...row, slabSequence: rowIndex + 1 }))
            return {
                ...prev,
                slabs: next.length > 0 ? next : [{ slabSequence: 1, revenueFrom: 0, revenueTo: '', commissionPercent: '', slabMode: 'ON_INCREMENTAL' }],
            }
        })
    }

    const updateSlabRow = (index, field, value) => {
        setFormData((prev) => {
            const nextSlabs = prev.slabs.map((slab, rowIndex) =>
                rowIndex === index ? { ...slab, [field]: value } : slab
            )
            // Auto-sync: revenueTo[n] → revenueFrom[n+1] so slabs stay contiguous
            if (field === 'revenueTo' && value !== null && value !== '' && index < nextSlabs.length - 1) {
                nextSlabs[index + 1] = { ...nextSlabs[index + 1], revenueFrom: value }
            }
            return { ...prev, slabs: nextSlabs }
        })
        if (errors.slabs) setErrors((prev) => ({ ...prev, slabs: undefined }))
    }

    const addCategoryRow = () => {
        setFormData((prev) => ({
            ...prev,
            categoryRates: [
                ...prev.categoryRates,
                { serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' },
            ],
        }))
    }

    const removeCategoryRow = (index) => {
        setFormData((prev) => {
            const next = prev.categoryRates.filter((_, rowIndex) => rowIndex !== index)
            return {
                ...prev,
                categoryRates: next.length > 0 ? next : [{ serviceCategoryName: '', serviceCategoryCode: '', commissionRate: '' }],
            }
        })
    }

    const updateCategoryRow = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            categoryRates: prev.categoryRates.map((row, rowIndex) => (
                rowIndex === index ? { ...row, [field]: value } : row
            )),
        }))
        if (errors.categoryRates) setErrors((prev) => ({ ...prev, categoryRates: undefined }))
    }

    const validate = () => {
        const nextErrors = {}

        if (!selectedType) nextErrors.componentType = 'Please select a commission type.'
        if (!formData.componentName.trim()) nextErrors.componentName = 'Component name is required.'

        if (selectedType === 'SERVICE_COMMISSION' || selectedType === 'RETAIL_COMMISSION') {
            const rate = Number(formData.rateValue)
            if (!rate || rate <= 0 || rate > 100) {
                nextErrors.rateValue = 'Enter a commission % between 0.01 and 100.'
            }
        }

        if (selectedType === 'SLAB_COMMISSION') {
            const hasTopSlab = formData.slabs.some((slab) => slab.revenueTo === null || slab.revenueTo === '')
            if (!hasTopSlab) nextErrors.slabs = 'The last slab must have no upper limit (No limit).'

            const hasMissingPercent = formData.slabs.some((slab) => !slab.commissionPercent || Number(slab.commissionPercent) <= 0)
            if (hasMissingPercent) nextErrors.slabs = 'All slabs must have a commission %.'
        }

        if (selectedType === 'CATEGORY_COMMISSION') {
            if (!formData.categoryRates.length) {
                nextErrors.categoryRates = 'Add at least one category.'
            } else if (formData.categoryRates.some((row) => !row.serviceCategoryCode.trim() || !row.commissionRate || Number(row.commissionRate) <= 0)) {
                nextErrors.categoryRates = 'All categories must have a code and commission %.'
            }
        }

        if (selectedType === 'TARGET_BONUS') {
            if (!formData.targetAmount || Number(formData.targetAmount) <= 0) {
                nextErrors.targetAmount = 'Enter a target amount.'
            }
            if (!formData.bonusValue || Number(formData.bonusValue) <= 0) {
                nextErrors.bonusValue = 'Enter a bonus value.'
            }
        }

        if (selectedType === 'ATTENDANCE_BONUS') {
            if (!formData.bonusValue || Number(formData.bonusValue) <= 0) {
                nextErrors.bonusValue = 'Enter a bonus amount.'
            }
        }

        return nextErrors
    }

    const addIncentiveComponent = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !templateIdentifier) return

        const nextErrors = validate()
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            return
        }

        setErrors({})

        try {
            setIsSaving(true)

            const payload = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
                templateIdentifier,
                componentName: formData.componentName.trim(),
                componentType: selectedType,
                computationBase: selectedType === 'ATTENDANCE_BONUS' ? 'FIXED' : formData.computationBase,
                rateValue: toNumber(formData.rateValue),
                targetAmount: toNumber(formData.targetAmount),
                targetPeriod: formData.targetPeriod,
                bonusValue: toNumber(formData.bonusValue),
                bonusValueType: formData.bonusValueType,
                minServiceCountForEligibility: parseInt(formData.minServiceCountForEligibility, 10) || 0,
                cappedAt: toNumber(formData.cappedAt),
            }

            if (formData.sortOrder !== '') {
                payload.sortOrder = parseInt(formData.sortOrder, 10) || 0
            }

            if (selectedType === 'SLAB_COMMISSION') {
                payload.slabs = formData.slabs.map((slab, index) => ({
                    slabSequence: index + 1,
                    revenueFrom: toNumber(slab.revenueFrom),
                    revenueTo: slab.revenueTo === '' || slab.revenueTo === null ? null : toNumber(slab.revenueTo),
                    commissionPercent: toNumber(slab.commissionPercent),
                    slabMode: slab.slabMode || 'ON_INCREMENTAL',
                }))
            }

            if (selectedType === 'CATEGORY_COMMISSION') {
                payload.categoryRates = formData.categoryRates.map((row) => ({
                    serviceCategoryName: row.serviceCategoryName.trim(),
                    serviceCategoryCode: row.serviceCategoryCode.trim().toUpperCase(),
                    commissionRate: toNumber(row.commissionRate),
                }))
            }

            const response = await fetch(BASE_URL_API + 'addIncentiveComponent', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success(`"${formData.componentName.trim()}" added.`)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (respo?.message?.toLowerCase().includes('componentname')) {
                        setErrors((prev) => ({ ...prev, componentName: respo.message }))
                    } else if (respo?.message?.toLowerCase().includes('slabs')) {
                        setErrors((prev) => ({ ...prev, slabs: respo.message }))
                    } else if (respo?.message?.toLowerCase().includes('categoryrates')) {
                        setErrors((prev) => ({ ...prev, categoryRates: respo.message }))
                    } else {
                        toast.error(respo?.message || 'Required field missing.')
                    }
                    break
                case 'VAL_002':
                    toast.error(respo?.message || 'Invalid value range.')
                    break
                case 'VAL_003':
                    toast.error(respo?.message || 'Invalid component type.')
                    break
                case 'COMP_006':
                    setErrors((prev) => ({ ...prev, slabs: respo?.message || 'Invalid slab configuration.' }))
                    break
                case 'COMP_007':
                    toast.error(respo?.message || 'Attendance bonus already exists.')
                    break
                case 'COMP_004':
                case 'VAL_008':
                    toast.error(respo?.message || 'Template not available for update.')
                    navigate(-1)
                    break
                case 'SYS_002':
                    toast.error(respo?.message || 'Failed to save component. Changes rolled back.')
                    break
                default:
                    toast.error(respo?.message || 'Failed to add component.')
                    break
            }
        } catch (error) {
            console.error('addIncentiveComponent error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const renderFormFields = () => {
        if (!selectedType) return null

        if (selectedType === 'SERVICE_COMMISSION' || selectedType === 'RETAIL_COMMISSION') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Commission % <span className={styles.required}>*</span></label>
                        <input
                            className={`${styles.textInput} ${errors.rateValue ? styles.inputError : ''}`}
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={formData.rateValue}
                            onChange={(event) => setField('rateValue', event.target.value)}
                            placeholder="e.g. 12"
                        />
                        {errors.rateValue && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.rateValue}</p>}
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Min Services</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="1"
                                value={formData.minServiceCountForEligibility}
                                onChange={(event) => setField('minServiceCountForEligibility', event.target.value)}
                                placeholder="0"
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Cap Amount (₹)</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.cappedAt}
                                onChange={(event) => setField('cappedAt', event.target.value)}
                                placeholder="0 = No cap"
                            />
                        </div>
                    </div>
                </>
            )
        }

        if (selectedType === 'SLAB_COMMISSION') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="RETAIL_REVENUE">Retail Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                        </select>
                    </div>

                    <div className={styles.builderCard}>
                        <div className={styles.builderHeader}>
                            <p>Slab Builder</p>
                            <button type="button" className={styles.addRowBtn} onClick={addSlabRow}>
                                <Plus size={13} /> Add Slab
                            </button>
                        </div>

                        {formData.slabs.map((slab, index) => {
                            const isLast = index === formData.slabs.length - 1
                            return (
                                <div key={`slab-${index}`} className={styles.builderRow}>
                                    <div className={styles.builderGridSlab}>
                                        <input
                                            className={styles.textInput}
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={slab.revenueFrom}
                                            onChange={(event) => updateSlabRow(index, 'revenueFrom', event.target.value)}
                                            placeholder="From ₹"
                                        />

                                        <div className={styles.revenuToWrap}>
                                            {slab.revenueTo === null ? (
                                                <span className={styles.noLimitDisplay}>∞ No limit</span>
                                            ) : (
                                                <input
                                                    className={styles.textInput}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={slab.revenueTo}
                                                    onChange={(event) => updateSlabRow(index, 'revenueTo', event.target.value)}
                                                    placeholder="To ₹"
                                                />
                                            )}
                                            {isLast && (
                                                <button
                                                    type="button"
                                                    className={`${styles.noLimitToggle} ${slab.revenueTo === null ? styles.noLimitToggleActive : ''}`}
                                                    onClick={() => updateSlabRow(index, 'revenueTo', slab.revenueTo === null ? '' : null)}
                                                    title={slab.revenueTo === null ? 'Set upper limit' : 'Set no upper limit'}
                                                >
                                                    ∞
                                                </button>
                                            )}
                                        </div>

                                        <input
                                            className={styles.textInput}
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={slab.commissionPercent}
                                            onChange={(event) => updateSlabRow(index, 'commissionPercent', event.target.value)}
                                            placeholder="Commission %"
                                        />

                                        <select
                                            className={styles.selectInput}
                                            value={slab.slabMode}
                                            onChange={(event) => updateSlabRow(index, 'slabMode', event.target.value)}
                                        >
                                            <option value="ON_INCREMENTAL">Incremental</option>
                                            <option value="ON_TOTAL">On Total</option>
                                        </select>
                                    </div>

                                    <button
                                        type="button"
                                        className={styles.removeRowBtn}
                                        onClick={() => removeSlabRow(index)}
                                        title="Remove slab"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            )
                        })}

                        {errors.slabs && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.slabs}</p>}
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Cap Amount (₹)</label>
                        <input
                            className={styles.textInput}
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.cappedAt}
                            onChange={(event) => setField('cappedAt', event.target.value)}
                            placeholder="0 = No cap"
                        />
                    </div>

                    <div className={styles.simCard}>
                        <div className={styles.simCardHeader}>
                            <p><Calculator size={14} /> Live Slab Preview</p>
                            {isSimulating && (
                                <span className={styles.simLoadingHint}>
                                    <LoaderCircle size={12} className={styles.spin} /> Simulating...
                                </span>
                            )}
                        </div>

                        <div className={styles.simInputRow}>
                            <label className={styles.simInputLabel}>Simulate revenue</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="0.01"
                                value={simulateRevenueInput}
                                onChange={(event) => setSimulateRevenueInput(event.target.value)}
                                placeholder="e.g. 35000"
                            />
                        </div>

                        <SlabSimulationResult result={simulationResult} />
                    </div>
                </>
            )
        }

        if (selectedType === 'CATEGORY_COMMISSION') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                        </select>
                    </div>

                    <div className={styles.builderCard}>
                        <div className={styles.builderHeader}>
                            <p>Category Rates</p>
                            <button type="button" className={styles.addRowBtn} onClick={addCategoryRow}>
                                <Plus size={13} /> Add Category
                            </button>
                        </div>

                        {formData.categoryRates.map((row, index) => (
                            <div key={`category-${index}`} className={styles.builderRow}>
                                <div className={styles.builderGridCategory}>
                                    <input
                                        className={styles.textInput}
                                        value={row.serviceCategoryName}
                                        onChange={(event) => updateCategoryRow(index, 'serviceCategoryName', event.target.value)}
                                        placeholder="Category name"
                                    />
                                    <input
                                        className={styles.textInput}
                                        value={row.serviceCategoryCode}
                                        onChange={(event) => updateCategoryRow(index, 'serviceCategoryCode', event.target.value.toUpperCase())}
                                        placeholder="Code (HAIR)"
                                    />
                                    <input
                                        className={styles.textInput}
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={row.commissionRate}
                                        onChange={(event) => updateCategoryRow(index, 'commissionRate', event.target.value)}
                                        placeholder="Commission %"
                                    />
                                </div>

                                <button
                                    type="button"
                                    className={styles.removeRowBtn}
                                    onClick={() => removeCategoryRow(index)}
                                    title="Remove category"
                                >
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        ))}

                        {errors.categoryRates && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.categoryRates}</p>}
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Cap Amount (₹)</label>
                        <input
                            className={styles.textInput}
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.cappedAt}
                            onChange={(event) => setField('cappedAt', event.target.value)}
                            placeholder="0 = No cap"
                        />
                    </div>
                </>
            )
        }

        if (selectedType === 'TARGET_BONUS') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="RETAIL_REVENUE">Retail Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                        </select>
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Target Amount (₹) <span className={styles.required}>*</span></label>
                            <input
                                className={`${styles.textInput} ${errors.targetAmount ? styles.inputError : ''}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.targetAmount}
                                onChange={(event) => setField('targetAmount', event.target.value)}
                                placeholder="e.g. 30000"
                            />
                            {errors.targetAmount && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.targetAmount}</p>}
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Target Period</label>
                            <select
                                className={styles.selectInput}
                                value={formData.targetPeriod}
                                onChange={(event) => setField('targetPeriod', event.target.value)}
                            >
                                <option value="MONTHLY">Monthly</option>
                                <option value="WEEKLY">Weekly</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Bonus Value <span className={styles.required}>*</span></label>
                            <input
                                className={`${styles.textInput} ${errors.bonusValue ? styles.inputError : ''}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.bonusValue}
                                onChange={(event) => setField('bonusValue', event.target.value)}
                                placeholder="e.g. 500"
                            />
                            {errors.bonusValue && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.bonusValue}</p>}
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Bonus Type</label>
                            <select
                                className={styles.selectInput}
                                value={formData.bonusValueType}
                                onChange={(event) => setField('bonusValueType', event.target.value)}
                            >
                                <option value="FLAT">Flat Amount</option>
                                <option value="PERCENT_OF_EXCESS">% of Excess</option>
                            </select>
                        </div>
                    </div>
                </>
            )
        }

        if (selectedType === 'ATTENDANCE_BONUS') {
            return (
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Bonus Amount (₹) <span className={styles.required}>*</span></label>
                    <input
                        className={`${styles.textInput} ${errors.bonusValue ? styles.inputError : ''}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.bonusValue}
                        onChange={(event) => setField('bonusValue', event.target.value)}
                        placeholder="e.g. 300"
                    />
                    {errors.bonusValue && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.bonusValue}</p>}
                </div>
            )
        }

        if (selectedType === 'CUSTOM_INCENTIVE') {
            return (
                <>
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Computation Base</label>
                        <select
                            className={styles.selectInput}
                            value={formData.computationBase}
                            onChange={(event) => setField('computationBase', event.target.value)}
                        >
                            <option value="SERVICE_REVENUE">Service Revenue</option>
                            <option value="RETAIL_REVENUE">Retail Revenue</option>
                            <option value="TOTAL_REVENUE">Total Revenue</option>
                            <option value="FIXED">Fixed</option>
                        </select>
                    </div>

                    <div className={styles.splitRow}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Commission %</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={formData.rateValue}
                                onChange={(event) => setField('rateValue', event.target.value)}
                                placeholder="0 if flat"
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Flat Amount (₹)</label>
                            <input
                                className={styles.textInput}
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.bonusValue}
                                onChange={(event) => setField('bonusValue', event.target.value)}
                                placeholder="0 if %"
                            />
                        </div>
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Cap Amount (₹)</label>
                        <input
                            className={styles.textInput}
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.cappedAt}
                            onChange={(event) => setField('cappedAt', event.target.value)}
                            placeholder="0 = No cap"
                        />
                    </div>
                </>
            )
        }

        return null
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Add Commission Rule</span>
                    <h1 className={styles.headerTitle}>{routeTemplateName}</h1>
                </div>
            </header>

            <div className={styles.formWrap}>
                <div className={styles.contextBanner}>
                    <Zap size={16} className={styles.contextBannerIcon} />
                    <p>
                        Add a rule to define how staff earn incentive pay.
                        Rules can be percentage, slab, category-wise, or target-based bonuses.
                    </p>
                </div>

                <div className={styles.typeSection}>
                    <div className={styles.typeSectionTitleRow}>
                        <p className={styles.typeSectionTitle}>Commission Type</p>
                        {errors.componentType && (
                            <p className={styles.fieldErrorInline}><AlertTriangle size={12} /> {errors.componentType}</p>
                        )}
                    </div>

                    <div className={styles.typeGrid}>
                        {COMPONENT_OPTIONS.map((option) => {
                            const active = selectedType === option.type
                            const palette = TYPE_COLORS[option.type] || { color: '#546e7a', bg: '#eceff1' }
                            return (
                                <button
                                    key={option.type}
                                    type="button"
                                    className={`${styles.typeCard} ${active ? styles.typeCardActive : ''}`}
                                    onClick={() => handleTypeSelect(option.type)}
                                >
                                    <span
                                        className={styles.typeCardBadge}
                                        style={{ color: palette.color, background: palette.bg }}
                                    >
                                        {option.label}
                                    </span>
                                    <p className={styles.typeCardDesc}>{option.description}</p>
                                </button>
                            )
                        })}
                    </div>
                </div>

                {selectedType && (
                    <div className={styles.formCard}>
                        <div className={styles.formCardHeader}>
                            <h3>{selectedTypeMeta?.label || 'Commission Rule'}</h3>
                            <ChevronRight size={15} />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Component Name <span className={styles.required}>*</span></label>
                            <input
                                className={`${styles.textInput} ${errors.componentName ? styles.inputError : ''}`}
                                value={formData.componentName}
                                onChange={(event) => setField('componentName', event.target.value)}
                                placeholder="e.g. Service Commission"
                                maxLength={200}
                            />
                            {errors.componentName && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.componentName}</p>}
                        </div>

                        {renderFormFields()}

                        <div className={styles.splitRow}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Sort Order</label>
                                <input
                                    className={styles.textInput}
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={formData.sortOrder}
                                    onChange={(event) => setField('sortOrder', event.target.value)}
                                    placeholder="Auto if empty"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.infoBanner}>
                    <Info size={14} className={styles.infoBannerIcon} />
                    <p>
                        For slab commission, keep exactly one top slab with <strong>No limit</strong>.
                        For attendance bonus, only one active attendance rule is allowed per plan.
                    </p>
                </div>

                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving}
                    onClick={addIncentiveComponent}
                >
                    {isSaving
                        ? <><LoaderCircle size={16} className={styles.spin} /> Adding...</>
                        : <>Add Commission Rule</>}
                </button>
            </div>
        </div>
    )
}

export default AddIncentiveComponentScreen